// `XMLText.escape` (SuiteCompile/XMLText.swift) and `XMLTree` (SuitePreflight/EpubCheck.swift): text made safe for
// XML, and a small well-formedness-checking XML parser with line numbers (NSXMLParser's rules as EPUB and DOCX meet
// them: one root, matched tags, quoted and unique attributes, only the five predefined entities and character
// references). Names are kept qualified, as NSXMLParser reports them without namespace processing.

/** Markup characters escaped; the characters XML 1.0 forbids turned into a space (U+000B, U+000C) or dropped. */
export function xmlEscape(s: string): string {
  let out = '';
  for (const ch of s) {
    const v = ch.codePointAt(0)!;
    switch (ch) {
      case '&': out += '&amp;'; break;
      case '<': out += '&lt;'; break;
      case '>': out += '&gt;'; break;
      case '"': out += '&quot;'; break;
      case '\t': case '\n': case '\r': out += ch; break;
      case '\u000b': case '\u000c': out += ' '; break;
      default:
        if (v < 0x20 || v === 0xfffe || v === 0xffff) continue;
        // A lone surrogate is repaired to U+FFFD, as Swift's strings do.
        out += v >= 0xd800 && v <= 0xdfff ? '�' : ch;
    }
  }
  return out;
}

export class XMLNode {
  children: XMLNode[] = [];
  text = '';
  constructor(readonly name: string, readonly attributes: Record<string, string>, readonly line: number) {}
  /** The text inside this element and every element below it. */
  get allText(): string { return this.text + this.children.map((c) => c.allText).join(''); }
  /** Every element below this one, in document order, optionally only those named `name`. */
  descendants(name?: string): XMLNode[] {
    const found: XMLNode[] = [];
    const walk = (n: XMLNode) => { for (const c of n.children) { if (name === undefined || c.name === name) found.push(c); walk(c); } };
    walk(this);
    return found;
  }
}

export class XMLFailure extends Error {
  constructor(readonly reason: string, readonly line: number) {
    super(`${reason} (line ${line})`);
    this.name = 'XMLFailure';
  }
}

const NAME_START = /[A-Za-z_:À-ÖØ-öø-˿Ͱ-ͽͿ-῿‌-‍⁰-↏Ⰰ-⿯、-퟿豈-﷏ﷰ-�]/;
const NAME_CHAR = /[-A-Za-z0-9_:.·À-ÖØ-öø-ͽͿ-῿‌-‍‿-⁀⁰-↏Ⰰ-⿯、-퟿豈-﷏ﷰ-�]/;
const PREDEFINED: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Characters XML 1.0 allows. */
function allowedChar(cp: number): boolean {
  return cp === 0x9 || cp === 0xa || cp === 0xd || (cp >= 0x20 && cp <= 0xd7ff) || (cp >= 0xe000 && cp <= 0xfffd) || (cp >= 0x10000 && cp <= 0x10ffff);
}

/** A parsed document: its root element. Throws `XMLFailure` for anything not well-formed. */
export class XMLTree {
  readonly root: XMLNode;

  constructor(input: string | Uint8Array) {
    const text = typeof input === 'string' ? input : new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(input);
    this.root = parseXML(text);
  }
}

function parseXML(src: string): XMLNode {
  let i = 0;
  let line = 1;
  const fail = (reason: string): never => { throw new XMLFailure(reason, line); };
  const advance = (n: number) => {
    for (let k = 0; k < n; k++) if (src.charCodeAt(i + k) === 10) line++;
    i += n;
  };
  const startsWith = (s: string) => src.startsWith(s, i);
  const skipSpace = () => { while (i < src.length && /[ \t\r\n]/.test(src[i])) advance(1); };
  const readName = (): string => {
    const start = i;
    const first = src.codePointAt(i);
    if (first === undefined || !NAME_START.test(String.fromCodePoint(first))) fail('a name was expected');
    advance(String.fromCodePoint(first!).length);
    while (i < src.length) {
      const cp = src.codePointAt(i)!;
      const ch = String.fromCodePoint(cp);
      if (!NAME_CHAR.test(ch) && !(cp >= 0x10000 && cp <= 0xeffff)) break;
      advance(ch.length);
    }
    return src.slice(start, i);
  };
  const decode = (raw: string, inAttribute: boolean): string => {
    let out = '';
    for (let k = 0; k < raw.length; k++) {
      const c = raw[k];
      if (c === '<' && inAttribute) fail('“<” in an attribute value');
      if (c !== '&') {
        const cp = raw.codePointAt(k)!;
        if (!allowedChar(cp)) fail(`a character XML doesn't allow (U+${cp.toString(16).toUpperCase().padStart(4, '0')})`);
        if (cp > 0xffff) { out += raw.slice(k, k + 2); k++; continue; }
        out += inAttribute && (c === '\n' || c === '\t' || c === '\r') ? ' ' : c;
        continue;
      }
      const end = raw.indexOf(';', k);
      if (end < 0) fail('an entity reference isn\'t closed');
      const name = raw.slice(k + 1, end);
      if (name.startsWith('#')) {
        const code = name[1] === 'x' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
        if (!/^#(x[0-9a-fA-F]+|[0-9]+)$/.test(name) || !allowedChar(code)) fail(`“&${name};” isn't a character XML allows`);
        out += String.fromCodePoint(code);
      } else if (PREDEFINED[name] !== undefined) {
        out += PREDEFINED[name];
      } else {
        fail(`the entity “&${name};” isn't defined`);
      }
      k = end;
    }
    return out;
  };

  // Prolog
  if (src.charCodeAt(0) === 0xfeff) i = 1;
  let root: XMLNode | null = null;
  const stack: XMLNode[] = [];
  let sawDoctype = false;

  const misc = (): boolean => {
    if (startsWith('<?')) {
      const end = src.indexOf('?>', i + 2);
      if (end < 0) fail('a processing instruction isn\'t closed');
      const target = src.slice(i + 2).match(/^[^\s?]+/)?.[0] ?? '';
      if (target.toLowerCase() === 'xml' && (i > (src.charCodeAt(0) === 0xfeff ? 1 : 0))) fail('the XML declaration must come first');
      advance(end + 2 - i);
      return true;
    }
    if (startsWith('<!--')) {
      const end = src.indexOf('-->', i + 4);
      if (end < 0) fail('a comment isn\'t closed');
      if (src.slice(i + 4, end).includes('--')) fail('“--” inside a comment');
      advance(end + 3 - i);
      return true;
    }
    return false;
  };

  while (i < src.length) {
    if (stack.length === 0) {
      // Outside the root: whitespace, comments, processing instructions, one doctype before the root.
      if (/[ \t\r\n]/.test(src[i])) { advance(1); continue; }
      if (misc()) continue;
      if (startsWith('<!DOCTYPE')) {
        if (sawDoctype || root) fail('a misplaced DOCTYPE');
        sawDoctype = true;
        let depth = 0;
        while (i < src.length) {
          const c = src[i];
          if (c === '[') depth++;
          else if (c === ']') depth--;
          else if (c === '>' && depth <= 0) { advance(1); break; }
          advance(1);
        }
        continue;
      }
      if (src[i] !== '<') fail(root ? 'content after the root element' : 'text before the root element');
      if (root) fail('more than one root element');
    }
    if (stack.length > 0 && src[i] !== '<') {
      const end = src.indexOf('<', i);
      const raw = src.slice(i, end < 0 ? src.length : end);
      if (raw.includes(']]>')) fail('“]]>” in text');
      stack[stack.length - 1].text += decode(raw, false);
      advance(raw.length);
      continue;
    }
    // Markup
    if (stack.length > 0 && misc()) continue;
    if (startsWith('<![CDATA[')) {
      if (!stack.length) fail('CDATA outside the root');
      const end = src.indexOf(']]>', i);
      if (end < 0) fail('a CDATA section isn\'t closed');
      advance(end + 3 - i);
      continue;
    }
    if (startsWith('</')) {
      advance(2);
      const name = readName();
      skipSpace();
      if (src[i] !== '>') fail(`the end tag </${name}> isn't closed`);
      advance(1);
      const open = stack.pop();
      if (!open) fail(`an end tag </${name}> with no start`);
      if (open!.name !== name) fail(`</${name}> ends <${open!.name}>`);
      continue;
    }
    if (startsWith('<!')) fail('unexpected markup “<!”');
    advance(1);
    const tagLine = line;
    const name = readName();
    const attributes: Record<string, string> = {};
    let selfClosing = false;
    for (;;) {
      const before = i;
      skipSpace();
      if (startsWith('/>')) { advance(2); selfClosing = true; break; }
      if (src[i] === '>') { advance(1); break; }
      if (i >= src.length) fail(`the start tag <${name}> isn't closed`);
      if (i === before) fail(`attributes of <${name}> must be separated by spaces`);
      const attr = readName();
      skipSpace();
      if (src[i] !== '=') fail(`the attribute ${attr} has no value`);
      advance(1);
      skipSpace();
      const quote = src[i];
      if (quote !== '"' && quote !== "'") fail(`the value of ${attr} isn't quoted`);
      const end = src.indexOf(quote, i + 1);
      if (end < 0) fail(`the value of ${attr} isn't closed`);
      const raw = src.slice(i + 1, end);
      if (attributes[attr] !== undefined) fail(`the attribute ${attr} appears twice`);
      attributes[attr] = decode(raw, true);
      advance(end + 1 - i);
    }
    const node = new XMLNode(name, attributes, tagLine);
    if (stack.length) stack[stack.length - 1].children.push(node);
    else root = node;
    if (!selfClosing) stack.push(node);
  }
  if (stack.length) fail(`<${stack[stack.length - 1].name}> isn't closed`);
  if (!root) fail('the document has no root element');
  return root!;
}

/** Whether `text` is well-formed XML (for tests and checks). */
export function isWellFormed(text: string | Uint8Array): boolean {
  try { new XMLTree(text); return true; } catch { return false; }
}
